import { useCallback } from 'react';

export interface SkipLinkProps {
  /**
   * The ID of the target element to skip to.
   * Defaults to 'main-content'.
   */
  targetId?: string;

  /**
   * The label text for the skip link.
   * Defaults to 'Skip to main content'.
   */
  label?: string;
}

/**
 * Skip-to-main-content link component.
 * 
 * Renders as the first focusable element in the page. When activated,
 * it moves focus to the main content area, allowing keyboard users
 * to bypass repetitive navigation.
 * 
 * The link is visually hidden until focused (via Tab key), at which point
 * it becomes visible at the top of the viewport.
 * 
 * Per Requirement 17.7: The SPA SHALL structure each page with a
 * skip-to-main-content link as the first focusable element.
 * 
 * Note: The skip link in index.html handles the initial page load.
 * This React component provides the same functionality within the
 * React tree for SPA navigation, ensuring focus management works
 * correctly after client-side route changes.
 */
export function SkipLink({
  targetId = 'main-content',
  label = 'Skip to main content',
}: SkipLinkProps) {
  const focusTarget = useCallback(() => {
    const target = document.getElementById(targetId);
    if (target) {
      // Ensure the target is focusable
      if (!target.hasAttribute('tabindex')) {
        target.setAttribute('tabindex', '-1');
      }
      target.focus();
      // Scroll the target into view (scrollIntoView may not exist in test environments)
      if (typeof target.scrollIntoView === 'function') {
        target.scrollIntoView({ block: 'start' });
      }
    }
  }, [targetId]);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      focusTarget();
    },
    [focusTarget],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        focusTarget();
      }
    },
    [focusTarget],
  );

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-[9999] focus:rounded focus:bg-brand-700 focus:px-4 focus:py-2 focus:text-white focus:outline-2 focus:outline-offset-2 focus:outline-brand-700"
      data-testid="skip-link"
    >
      {label}
    </a>
  );
}
